import React, { useState, useCallback, useEffect } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
  BreadcrumbGroup,
  SpaceBetween,
  Button,
  ContentLayout,
  Box,
  Container,
  Header,
  Table,
  TextFilter,
  Badge,
  StatusIndicator,
  Link,
  Icon,
  Alert,
  Toggle,
  Pagination,
} from '@amzn/awsui-components-console';
import { useParams, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { getUserAtom } from '../../contexts/userContext';
import { makeApiCall } from '../../utils/api';
import { API_PATHS } from '../../config/api-endpoints';
import { useProfile } from '../../hooks/useProfiles';
import { useSchedules, ScheduleFormData } from '../../hooks/useSchedules';
import { Schedule, ScheduleStatus } from '../../types';
import CreateScheduleModal from '../molecules/CreateScheduleModal';
import EditScheduleModal from '../molecules/EditScheduleModal';
import DeleteConfirmModal from '../molecules/DeleteConfirmModal';
import NProgress from 'nprogress';
import './ScheduleListPage.scss';

/**
 * ScheduleListPage Component
 *
 * Displays a paginated list of schedules for a specific job.
 * Provides functionality to:
 * - View schedules in a table with Name, Cron, Status, Next Execution, Actions columns
 * - Filter schedules by text
 * - Create, edit, and delete schedules via modals
 * - Toggle schedule enabled/disabled state
 * - Load more schedules with pagination
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 5.1
 */
const ScheduleListPage: React.FC = () => {
  const { id: profileId, jobId } = useParams();
  const navigate = useNavigate();
  const [authenticatedUser] = useAtom(getUserAtom);

  // Fetch profile data from API
  const { profile: profileData, loading: profileLoading, error: profileError } = useProfile(
    profileId || null
  );

  // Fetch schedules using the useSchedules hook
  const {
    schedules,
    loading,
    error,
    hasMore,
    fetchMore,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    updateScheduleStatus,
    refetch,
  } = useSchedules({ jobId: jobId || '', enabled: !!jobId });

  // Job details state
  const [jobData, setJobData] = useState<{
    id: string;
    name: string;
    description?: string;
  } | null>(null);
  const [jobLoading, setJobLoading] = useState(false);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);

  // Toggle loading state (per schedule)
  const [togglingScheduleId, setTogglingScheduleId] = useState<string | null>(null);

  // Success/error notifications
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Use collection hook for filtering, sorting, and pagination
  const { items, actions, filteredItemsCount, collectionProps, filterProps, paginationProps } = useCollection(
    schedules,
    {
      filtering: {
        empty: (
          <Box textAlign="center" color="text-status-inactive" padding={{ vertical: 'xxl' }}>
            <SpaceBetween size="m">
              <Icon name="calendar" size="large" />
              <b>No schedules found</b>
              <Box>Create your first schedule to automate job execution</Box>
              <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                Create Schedule
              </Button>
            </SpaceBetween>
          </Box>
        ),
        noMatch: (
          <Box textAlign="center" color="text-status-inactive" padding={{ vertical: 'xxl' }}>
            <SpaceBetween size="m">
              <Icon name="search" size="large" />
              <b>No matches</b>
              <Box>Try adjusting your filter</Box>
              <Button onClick={() => actions.setFiltering('')}>Clear filter</Button>
            </SpaceBetween>
          </Box>
        ),
      },
      pagination: { pageSize: 10 },
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: 'createdAt',
          },
          isDescending: true,
        },
      },
      selection: {},
    }
  );

  /**
   * Fetch job details
   */
  const fetchJobDetails = useCallback(async () => {
    if (!jobId || !authenticatedUser) return;

    setJobLoading(true);
    try {
      const response = await makeApiCall(API_PATHS.JOBS.GET, 'POST', {
        userId: authenticatedUser.username,
        jobId: jobId,
      });

      if (response.job) {
        setJobData({
          id: response.job.jobId,
          name: response.job.jobName,
          description: response.job.description,
        });
      }
    } catch (error: any) {
      console.error('Error fetching job details:', error);
      // Don't show error for job details, just use jobId as fallback
    } finally {
      setJobLoading(false);
    }
  }, [jobId, authenticatedUser]);

  // Fetch job details on mount
  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    NProgress.start();
    refetch().finally(() => NProgress.done());
  };

  /**
   * Handle load more
   */
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      NProgress.start();
      fetchMore().finally(() => NProgress.done());
    }
  };

  /**
   * Handle create schedule
   */
  const handleCreateSchedule = async (data: ScheduleFormData) => {
    try {
      await createSchedule(data);
      setNotification({ type: 'success', message: 'Schedule created successfully!' });
      setShowCreateModal(false);
    } catch (err: any) {
      // Error is handled in the modal
      throw err;
    }
  };

  /**
   * Handle edit schedule
   */
  const handleEditSchedule = async (data: ScheduleFormData) => {
    if (!selectedSchedule) return;
    try {
      await updateSchedule(selectedSchedule.scheduleId, data);
      setNotification({ type: 'success', message: 'Schedule updated successfully!' });
      setShowEditModal(false);
      setSelectedSchedule(null);
    } catch (err: any) {
      // Error is handled in the modal
      throw err;
    }
  };

  /**
   * Handle delete schedule
   */
  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    try {
      await deleteSchedule(selectedSchedule.scheduleId);
      setNotification({ type: 'success', message: 'Schedule deleted successfully!' });
      setShowDeleteModal(false);
      setSelectedSchedule(null);
    } catch (err: any) {
      // Error is handled in the modal
      throw err;
    }
  };

  /**
   * Handle toggle schedule status
   */
  const handleToggleSchedule = async (schedule: Schedule, enabled: boolean) => {
    setTogglingScheduleId(schedule.scheduleId);
    try {
      await updateScheduleStatus(schedule.scheduleId, enabled);
      setNotification({
        type: 'success',
        message: `Schedule ${enabled ? 'enabled' : 'disabled'} successfully!`,
      });
    } catch (err: any) {
      setNotification({
        type: 'error',
        message: err.message || 'Failed to update schedule status',
      });
    } finally {
      setTogglingScheduleId(null);
    }
  };

  /**
   * Open edit modal
   */
  const openEditModal = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setShowEditModal(true);
  };

  /**
   * Open delete modal
   */
  const openDeleteModal = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    setShowDeleteModal(true);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
        timeZoneName: 'short',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Status badge component
   * Requirement 1.2: Display status badges showing Enabled (green), Disabled (grey), or Auto-Disabled (error)
   */
  const StatusBadge = ({ status }: { status: ScheduleStatus }) => {
    switch (status) {
      case 'ENABLED':
        return <StatusIndicator type="success">Enabled</StatusIndicator>;
      case 'DISABLED':
        return <StatusIndicator type="stopped">Disabled</StatusIndicator>;
      case 'AUTO_DISABLED':
        return <StatusIndicator type="error">Auto-Disabled</StatusIndicator>;
      default:
        return <StatusIndicator>Unknown</StatusIndicator>;
    }
  };

  /**
   * Table column definitions
   * Requirement 1.1: Display table with columns: Name, Schedule Expression, Status, Next Execution, Actions
   */
  const columnDefinitions = [
    {
      id: 'name',
      header: 'Name',
      cell: (item: Schedule) => (
        <Link
          href="#"
          external={false}
          onFollow={(e) => {
            e.preventDefault();
            navigate(`/profile/${profileId}/job/${jobId}/schedules/${item.scheduleId}`);
          }}
        >
          {item.scheduleName || 'Unnamed Schedule'}
        </Link>
      ),
      sortingField: 'scheduleName',
      isRowHeader: true,
      width: 200,
    },
    {
      id: 'scheduleExpression',
      header: 'Schedule Expression',
      cell: (item: Schedule) => (
        <code
          style={{
            fontFamily: 'Monaco, Menlo, monospace',
            fontSize: '0.875rem',
            backgroundColor: '#f2f3f3',
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          {item.scheduleExpression}
        </code>
      ),
      sortingField: 'scheduleExpression',
      width: 180,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item: Schedule) => (
        <SpaceBetween size="xs" direction="vertical">
          <StatusBadge status={item.status} />
          {item.consecutiveFailures !== undefined && item.consecutiveFailures > 0 && (
            <Box fontSize="body-s" color="text-status-error">
              {item.consecutiveFailures} consecutive failure{item.consecutiveFailures > 1 ? 's' : ''}
            </Box>
          )}
        </SpaceBetween>
      ),
      sortingField: 'status',
      width: 140,
    },
    {
      id: 'nextExecution',
      header: 'Next Execution',
      cell: (item: Schedule) => formatDate(item.nextExecutionTime),
      sortingField: 'nextExecutionTime',
      width: 200,
    },
    {
      id: 'toggle',
      header: 'Enabled',
      cell: (item: Schedule) => (
        <Toggle
          checked={item.status === 'ENABLED'}
          onChange={({ detail }) => handleToggleSchedule(item, detail.checked)}
          disabled={togglingScheduleId === item.scheduleId || item.status === 'AUTO_DISABLED'}
        />
      ),
      width: 100,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (item: Schedule) => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" iconName="edit" onClick={() => openEditModal(item)}>
            Edit
          </Button>
          <Button variant="link" iconName="remove" onClick={() => openDeleteModal(item)}>
            Delete
          </Button>
        </SpaceBetween>
      ),
      width: 180,
    },
  ];

  return (
    <div className="schedule-list-page">
      <ContentLayout
        defaultPadding={false}
        headerVariant="high-contrast"
        header={
          <Box padding={{ horizontal: 'xxxl', vertical: 'l' }}>
            <SpaceBetween size="m" direction="vertical">
              {/* Breadcrumb Navigation - Requirement 9.3 */}
              <BreadcrumbGroup
                items={[
                  { text: 'Home', href: '/' },
                  { text: profileData?.name || 'Profile', href: `/profile/${profileId}` },
                  { text: 'Jobs', href: `/profile/${profileId}/jobs` },
                  { text: jobData?.name || jobId || 'Job', href: `/profile/${profileId}/job/${jobId}` },
                  { text: 'Schedules', href: '#' },
                ]}
                ariaLabel="Breadcrumbs"
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  width: '100%',
                }}
              >
                <div style={{ flex: 1 }}>
                  <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                    Schedules
                  </Box>
                  <Box fontSize="body-m" color="inherit" margin={{ top: 'xs' }}>
                    Manage automated schedules for {jobData?.name || 'this job'}
                  </Box>
                </div>
                <div style={{ display: 'flex', gap: '12px', marginLeft: 'auto' }}>
                  <Button
                    iconName="refresh"
                    onClick={handleRefresh}
                    variant="normal"
                    loading={loading}
                    ariaLabel="Refresh schedules"
                  />
                  {/* Create Schedule Button - Requirement 2.1 */}
                  <Button variant="primary" iconName="add-plus" onClick={() => setShowCreateModal(true)}>
                    Create Schedule
                  </Button>
                </div>
              </div>
            </SpaceBetween>
          </Box>
        }
      >
        <Box padding={{ horizontal: 'xl', vertical: 'l' }}>
          {/* Notification Alert */}
          {notification && (
            <Box margin={{ bottom: 'm' }}>
              <Alert
                type={notification.type}
                dismissible
                onDismiss={() => setNotification(null)}
                header={notification.type === 'success' ? 'Success' : 'Error'}
              >
                {notification.message}
              </Alert>
            </Box>
          )}

          {/* Auto-Disabled Schedules Alert */}
          {schedules.some((s) => s.status === 'AUTO_DISABLED') && (
            <Box margin={{ bottom: 'm' }}>
              <Alert
                type="warning"
                header="Schedules auto-disabled due to failures"
              >
                {schedules.filter((s) => s.status === 'AUTO_DISABLED').length} schedule(s) have been
                automatically disabled after {schedules.find((s) => s.status === 'AUTO_DISABLED')?.consecutiveFailures || 3} consecutive failures.
                Review the failure reasons and re-enable them manually after fixing the issues.
              </Alert>
            </Box>
          )}

          {/* Profile Error Alert */}
          {profileError && (
            <Box margin={{ bottom: 'm' }}>
              <Alert type="error" dismissible onDismiss={() => {}} header="Error loading profile">
                {profileError}
              </Alert>
            </Box>
          )}

          <Container
            header={
              <Header
                variant="h2"
                counter={`(${filteredItemsCount})`}
                description="View and manage job schedules"
              >
                Schedules
              </Header>
            }
          >
            <SpaceBetween size="l" direction="vertical">
              {/* Error Alert - Requirement 1.5 */}
              {error && (
                <Alert
                  type="error"
                  dismissible
                  onDismiss={() => {}}
                  header="Error loading schedules"
                  action={
                    <Button onClick={handleRefresh} loading={loading}>
                      Retry
                    </Button>
                  }
                >
                  {error}
                </Alert>
              )}

              {/* Text Filter */}
              <TextFilter
                {...filterProps}
                filteringPlaceholder="Find schedules by name, description, or schedule expression"
                filteringAriaLabel="Filter schedules"
                countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
              />

              {/* Schedules Table - Requirement 1.1 */}
              <Table
                {...collectionProps}
                columnDefinitions={columnDefinitions}
                items={items}
                loading={loading}
                loadingText="Loading schedules..."
                trackBy="scheduleId"
                variant="container"
                stickyHeader={true}
                sortingDescending={true}
                header={
                  <Header
                    counter={`(${filteredItemsCount})`}
                    actions={
                      <Pagination
                        {...paginationProps}
                        ariaLabels={{
                          nextPageLabel: 'Next page',
                          previousPageLabel: 'Previous page',
                          pageLabel: (pageNumber) => `Page ${pageNumber}`,
                        }}
                      />
                    }
                  >
                    Schedules
                  </Header>
                }
              />

              {/* Load More Button - Requirement 1.3 */}
              {hasMore && !loading && (
                <Box textAlign="center">
                  <Button onClick={handleLoadMore} loading={loading} loadingText="Loading more...">
                    Load more schedules
                  </Button>
                </Box>
              )}
            </SpaceBetween>
          </Container>

          {/* Add spacing at bottom to prevent footer overlap */}
          <Box padding={{ bottom: 'xxxl' }} />
        </Box>
      </ContentLayout>

      {/* Create Schedule Modal - Requirement 2.1 */}
      <CreateScheduleModal
        visible={showCreateModal}
        onDismiss={() => setShowCreateModal(false)}
        onSubmit={handleCreateSchedule}
        jobId={jobId || ''}
      />

      {/* Edit Schedule Modal - Requirement 3.1 */}
      {selectedSchedule && (
        <EditScheduleModal
          visible={showEditModal}
          onDismiss={() => {
            setShowEditModal(false);
            setSelectedSchedule(null);
          }}
          onSubmit={handleEditSchedule}
          schedule={selectedSchedule}
          jobId={jobId || ''}
        />
      )}

      {/* Delete Confirm Modal - Requirement 4.1 */}
      <DeleteConfirmModal
        visible={showDeleteModal}
        onDismiss={() => {
          setShowDeleteModal(false);
          setSelectedSchedule(null);
        }}
        onConfirm={handleDeleteSchedule}
        schedule={selectedSchedule}
      />
    </div>
  );
};

export default ScheduleListPage;
